(function () {
  const DESKTOP_MQ = window.matchMedia('(min-width: 64rem)');
  const RECENT_SEARCHES_KEY = 'map-recent-searches';
  const RECENT_SEARCHES_MAX = 5;

  /** @type {Window & { MapUI?: { getFocusable: (container: ParentNode) => HTMLElement[]; createFocusTrap: (container: HTMLElement, onEscape: () => void) => () => void } }} */
  const { MapUI } = window;

  /** @param {ParentNode} container */
  function getFocusable(container) {
    return MapUI?.getFocusable ? MapUI.getFocusable(container) : [];
  }

  /**
   * @param {HTMLElement} container
   * @param {() => void} onEscape
   */
  function createFocusTrap(container, onEscape) {
    if (MapUI?.createFocusTrap) return MapUI.createFocusTrap(container, onEscape);
    return () => {};
  }

  /** @param {unknown} url */
  function isUsableUrl(url) {
    if (typeof url !== 'string') return false;
    const trimmed = url.trim();
    if (!trimmed || trimmed === '#') return false;
    return !/^javascript:/i.test(trimmed);
  }

  /** @param {Event} event */
  function preventBackgroundScroll(event) {
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest('[data-header-drawer], [data-header-search-panel]')
    ) {
      return;
    }
    event.preventDefault();
  }

  function lockScroll() {
    if (document.documentElement.hasAttribute('data-header-scroll-lock')) return;
    document.documentElement.dataset.headerScrollLock = '';
    document.addEventListener('touchmove', preventBackgroundScroll, { passive: false });
    document.addEventListener('wheel', preventBackgroundScroll, { passive: false });
  }

  function unlockScroll() {
    delete document.documentElement.dataset.headerScrollLock;
    document.removeEventListener('touchmove', preventBackgroundScroll);
    document.removeEventListener('wheel', preventBackgroundScroll);
  }

  /** @param {HTMLElement} root */
  function initHeader(root) {
    const menuToggle = root.querySelector('[data-header-menu-toggle]');
    const drawer = root.querySelector('[data-header-drawer]');
    const backdrop = root.querySelector('[data-header-drawer-backdrop]');
    const drawerCloseBtn = root.querySelector('[data-header-drawer-close]');

    /** @type {(() => void) | null} */
    let releaseDrawerTrap = null;
    /** @type {HTMLElement | null} */
    let previousFocus = null;

    /** @type {(() => void) | null} */
    let resetMobileNav = null;
    /** @type {number | null} */
    let drawerHideTimer = null;

    const DRAWER_MS = 200;

    function prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    function isDrawerOpen() {
      return Boolean(drawer?.classList.contains('is-open'));
    }

    function finishDrawerClose() {
      drawerHideTimer = null;
      if (!drawer || isDrawerOpen()) return;

      drawer.hidden = true;
      if (backdrop) backdrop.hidden = true;
      root.classList.remove('is-menu-open');
      resetMobileNav?.();

      if (previousFocus instanceof HTMLElement) {
        previousFocus.focus({ preventScroll: true });
      }
      previousFocus = null;
    }

    function closeDrawer() {
      if (!drawer || (!isDrawerOpen() && drawer.hidden)) return;

      if (drawerHideTimer) {
        window.clearTimeout(drawerHideTimer);
        drawerHideTimer = null;
      }

      drawer.classList.remove('is-open');
      backdrop?.classList.remove('is-open');
      menuToggle?.setAttribute('aria-expanded', 'false');
      unlockScroll();
      releaseDrawerTrap?.();
      releaseDrawerTrap = null;

      const delay = prefersReducedMotion() ? 0 : DRAWER_MS;
      drawerHideTimer = window.setTimeout(finishDrawerClose, delay);
    }

    function openDrawer() {
      if (!drawer || !menuToggle || DESKTOP_MQ.matches) return;
      if (isDrawerOpen()) return;

      if (drawerHideTimer) {
        window.clearTimeout(drawerHideTimer);
        drawerHideTimer = null;
      }

      previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      syncHeaderOffset();
      root.classList.add('is-menu-open');
      drawer.hidden = false;
      if (backdrop) backdrop.hidden = false;
      menuToggle.setAttribute('aria-expanded', 'true');
      lockScroll();

      void drawer.offsetWidth;
      drawer.classList.add('is-open');
      backdrop?.classList.add('is-open');

      releaseDrawerTrap = createFocusTrap(drawer, closeDrawer);
      (drawerCloseBtn || getFocusable(drawer)[0])?.focus({ preventScroll: true });
    }

    menuToggle?.addEventListener('click', () => {
      if (isDrawerOpen()) closeDrawer();
      else openDrawer();
    });

    drawerCloseBtn?.addEventListener('click', closeDrawer);
    backdrop?.addEventListener('click', closeDrawer);

    DESKTOP_MQ.addEventListener('change', () => {
      if (DESKTOP_MQ.matches) closeDrawer();
    });

    /** @typedef {{ title: string, url: string, children?: MenuNode[] }} MenuNode */

    /**
     * Menu tree rendered once by header.liquid — shared by the desktop mega
     * flyout and the mobile drill-down.
     * @returns {MenuNode[]}
     */
    function readMenuData() {
      const dataEl = root.querySelector('[data-header-menu-json]');
      if (!(dataEl instanceof HTMLScriptElement)) return [];

      try {
        const parsed = JSON.parse(dataEl.textContent || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    const menuData = readMenuData();

    function viewAllLabel(title) {
      const template = root.dataset.headerViewAll || 'View all %title%';
      return template.replace(/%title%/g, title || '');
    }

    /**
     * A node renders only when it leads somewhere: a real URL or children.
     * @param {MenuNode} item
     */
    function isRenderableNode(item) {
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;
      return hasChildren || isUsableUrl(item.url);
    }

    /**
     * Children of a node, with a "View all {title}" link prepended when the
     * node itself has a URL — parents with children render as drill buttons,
     * so this keeps their own destination reachable (mobile and desktop).
     * @param {MenuNode | undefined} node
     * @returns {MenuNode[]}
     */
    function childItemsWithViewAll(node) {
      const items = (node?.children || []).slice();
      if (node && isUsableUrl(node.url)) {
        items.unshift({ title: viewAllLabel(node.title), url: node.url });
      }
      return items;
    }

    /** @param {HTMLElement} host */
    function initMegaFlyout(host) {
      const flyout = host.querySelector('[data-header-mega-flyout]');
      const triggers = host.querySelectorAll('[data-header-mega-trigger]');
      const cols = host.querySelectorAll('[data-mega-col]');

      if (!(flyout instanceof HTMLElement)) return;

      /** @type {number | null} */
      let activeRootIndex = null;
      let ignoreOutsideClick = false;

      /** @param {number} colIndex */
      function clearColumnsFrom(colIndex) {
        cols.forEach((col, index) => {
          if (!(col instanceof HTMLElement)) return;
          if (index >= colIndex) {
            col.replaceChildren();
            col.hidden = true;
          }
        });
      }

      /**
       * @param {number} colIndex
       * @param {MenuNode[]} items
       * @param {number} activeIndex -1 = none active (Figma default on open)
       * @param {(item: MenuNode) => void} onSelect
       */
      function renderColumn(colIndex, items, activeIndex, onSelect) {
        const col = cols[colIndex];
        if (!(col instanceof HTMLElement)) return;

        col.replaceChildren();

        const visible = items.filter(isRenderableNode);

        if (!visible.length) {
          col.hidden = true;
          return;
        }

        col.hidden = false;

        visible.forEach((item, itemIndex) => {
          const hasChildren = Array.isArray(item.children) && item.children.length > 0;
          const el = hasChildren ? document.createElement('button') : document.createElement('a');

          el.className = 'site-header__mega-flyout-item';
          if (hasChildren) {
            el.classList.add('has-children');
            if (el instanceof HTMLButtonElement) el.type = 'button';
          } else if (el instanceof HTMLAnchorElement) {
            el.href = item.url;
            el.classList.add('site-header__mega-flyout-item--link');
          }

          if (activeIndex >= 0 && itemIndex === activeIndex && hasChildren) {
            el.classList.add('is-active');
          }

          const label = document.createElement('span');
          label.className = 'site-header__mega-flyout-label';
          label.textContent = item.title;
          el.append(label);

          if (hasChildren) {
            const chevron = document.createElement('span');
            chevron.className = 'site-header__mega-flyout-chevron';
            chevron.setAttribute('aria-hidden', 'true');
            el.append(chevron);

            const activate = () => {
              col.querySelectorAll('.site-header__mega-flyout-item.has-children').forEach((node) => {
                node.classList.toggle('is-active', node === el);
              });
              onSelect(item);
              el.scrollIntoView({ block: 'nearest' });
            };

            el.addEventListener('mouseenter', activate);
            el.addEventListener('focus', activate);
            el.addEventListener('click', (event) => {
              event.preventDefault();
              activate();
            });
          }

          col.append(el);
        });
      }

      /**
       * Figma 4283:11092 — one active row per column, columns reveal on hover.
       * Open shows column 1 only; no pre-selected rows until pointer/focus.
       * Every column prepends its parent's "View all" link (same as mobile).
       *
       * @param {MenuNode} node - top-level menu node
       */
      function renderRootColumn(node) {
        renderColumn(0, childItemsWithViewAll(node), -1, (selected) => {
          if (!selected?.children?.length) {
            clearColumnsFrom(1);
            return;
          }

          renderColumn(1, childItemsWithViewAll(selected), -1, (sub) => {
            if (!sub?.children?.length) {
              clearColumnsFrom(2);
              return;
            }

            renderColumn(2, childItemsWithViewAll(sub), -1, () => {});
          });
        });
      }

      function setTriggerStates() {
        triggers.forEach((trigger) => {
          if (!(trigger instanceof HTMLElement)) return;
          const index = Number(trigger.dataset.megaIndex);
          const isActive = activeRootIndex === index;
          trigger.classList.toggle('is-active', isActive);

          const link = trigger.querySelector('[data-header-mega-control]');
          link?.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        });
      }

      /** @param {number} index */
      function openFlyout(index) {
        const node = menuData[index];
        if (!node || !childItemsWithViewAll(node).some(isRenderableNode)) return;

        activeRootIndex = index;
        flyout.hidden = false;
        flyout.setAttribute('aria-hidden', 'false');
        setTriggerStates();
        clearColumnsFrom(0);
        renderRootColumn(node);
      }

      function closeFlyout() {
        activeRootIndex = null;
        flyout.hidden = true;
        flyout.setAttribute('aria-hidden', 'true');
        clearColumnsFrom(0);
        setTriggerStates();
      }

      /** @param {number} index */
      function toggleFlyout(index) {
        if (activeRootIndex === index) {
          closeFlyout();
          return;
        }
        openFlyout(index);
      }

      triggers.forEach((trigger) => {
        if (!(trigger instanceof HTMLElement)) return;

        const index = Number(trigger.dataset.megaIndex);
        if (Number.isNaN(index)) return;

        const control = trigger.querySelector('[data-header-mega-control]');
        if (!(control instanceof HTMLElement)) return;

        control.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          ignoreOutsideClick = true;
          toggleFlyout(index);
          window.setTimeout(() => {
            ignoreOutsideClick = false;
          }, 0);
        });

        control.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          toggleFlyout(index);
        });
      });

      document.addEventListener('click', (event) => {
        if (ignoreOutsideClick) return;
        if (!(event.target instanceof Node)) return;
        if (activeRootIndex === null) return;
        if (host.contains(event.target)) return;
        closeFlyout();
      });

      host.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && activeRootIndex !== null) {
          closeFlyout();
        }
      });

      return closeFlyout;
    }

    /** @type {(() => void) | null} */
    let closeMegaFlyout = null;

    const megaHost = root.querySelector('[data-header-mega-host]');
    if (megaHost instanceof HTMLElement) {
      closeMegaFlyout = initMegaFlyout(megaHost) || null;
    }

    function closeAllMegaGroups() {
      closeMegaFlyout?.();
    }

    DESKTOP_MQ.addEventListener('change', closeAllMegaGroups);

    /**
     * @param {HTMLElement} drawerEl
     * @returns {(() => void) | undefined}
     */
    function initMobileNav(drawerEl) {
      const host = drawerEl.querySelector('[data-mobile-nav]');
      if (!(host instanceof HTMLElement)) return;

      const rootPanel = host.querySelector('[data-mobile-nav-panel="root"]');
      const subPanel = host.querySelector('[data-mobile-nav-panel="sub"]');
      const subList = host.querySelector('[data-mobile-nav-sublist]');
      const subTitle = host.querySelector('[data-mobile-nav-subtitle]');
      const backBtn = host.querySelector('[data-mobile-nav-back]');

      /** @type {{ title: string, items: MenuNode[] }[]} */
      let panelStack = [];

      function resetPanels() {
        panelStack = [];
        if (rootPanel instanceof HTMLElement) rootPanel.hidden = false;
        if (subPanel instanceof HTMLElement) subPanel.hidden = true;
      }

      /**
       * @param {MenuNode[]} items
       * @param {string} title
       * @param {boolean} [pushStack]
       */
      function renderSubPanel(items, title, pushStack = true) {
        if (
          !(subList instanceof HTMLElement) ||
          !(subTitle instanceof HTMLElement) ||
          !(rootPanel instanceof HTMLElement) ||
          !(subPanel instanceof HTMLElement)
        ) {
          return;
        }

        if (pushStack) panelStack.push({ title, items });

        subTitle.textContent = title;
        subList.replaceChildren();

        items.forEach((item) => {
          const li = document.createElement('li');
          li.className = 'site-header__mobile-nav-item';

          const hasChildren = Array.isArray(item.children) && item.children.length > 0;

          if (hasChildren) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'site-header__mobile-nav-row';
            const label = document.createElement('span');
            label.className = 'site-header__mobile-nav-label';
            label.textContent = item.title;
            btn.append(label);
            btn.addEventListener('click', () => {
              renderSubPanel(childItemsWithViewAll(item), item.title);
            });
            li.append(btn);
          } else if (isUsableUrl(item.url)) {
            const link = document.createElement('a');
            link.className = 'site-header__mobile-nav-row site-header__mobile-nav-row--link';
            link.href = item.url;
            const label = document.createElement('span');
            label.className = 'site-header__mobile-nav-label';
            label.textContent = item.title;
            link.append(label);
            li.append(link);
          } else {
            return;
          }

          subList.append(li);
        });

        rootPanel.hidden = true;
        subPanel.hidden = false;
      }

      host.querySelectorAll('[data-mobile-nav-drill]').forEach((trigger) => {
        if (!(trigger instanceof HTMLButtonElement)) return;

        trigger.addEventListener('click', () => {
          const index = Number(trigger.dataset.menuIndex);
          const branch = menuData[index];
          if (!branch?.children?.length) return;

          panelStack = [];
          renderSubPanel(childItemsWithViewAll(branch), branch.title);
        });
      });

      backBtn?.addEventListener('click', () => {
        if (panelStack.length > 1) {
          panelStack.pop();
          const previous = panelStack[panelStack.length - 1];
          renderSubPanel(previous.items, previous.title, false);
          return;
        }

        resetPanels();
      });

      return resetPanels;
    }

    if (drawer instanceof HTMLElement) {
      resetMobileNav = initMobileNav(drawer) || null;
    }

    const searchTrigger = root.querySelector('[data-header-search-trigger]');
    const searchPanel = root.querySelector('[data-header-search-panel]');
    const searchBackdrop = root.querySelector('[data-header-search-backdrop]');
    const searchForm = searchPanel?.querySelector('form');
    const searchInput = searchPanel?.querySelector('[data-header-search-input]');
    const searchField = searchPanel?.querySelector('.site-header__search-field');
    const searchClear = searchPanel?.querySelector('[data-header-search-clear]');
    const searchSubmit = searchPanel?.querySelector('[data-header-search-submit]');
    const searchSuggest = searchPanel?.querySelector('[data-header-search-suggest]');
    const searchQueries = searchPanel?.querySelector('[data-header-search-queries]');
    const searchProducts = searchPanel?.querySelector('[data-header-search-products]');
    const searchViewAll = searchPanel?.querySelector('[data-header-search-view-all]');
    const searchVehicleBtn = searchPanel?.querySelector('[data-header-vehicle-variant="search"]');
    const recentList = searchPanel?.querySelector('[data-header-search-recent-list]');
    const moneyFormat = searchSuggest instanceof HTMLElement ? searchSuggest.dataset.moneyFormat || '' : '';
    const gstRate =
      searchSuggest instanceof HTMLElement && window.MapUtils
        ? window.MapUtils.gstRateFromDataset(searchSuggest.dataset)
        : 1.1;
    const suitsPrefix = searchSuggest instanceof HTMLElement ? searchSuggest.dataset.suitsPrefix || 'Suits' : 'Suits';
    const searchUrl = searchForm instanceof HTMLFormElement ? searchForm.getAttribute('action') || '/search' : '/search';
    const desktopSuggestMq = window.matchMedia('(min-width: 64rem)');
    const SEARCH_MS = 150;
    /** @type {number | null} */
    let searchHideTimer = null;

    function isSearchOpen() {
      return root.classList.contains('is-search-open');
    }

    function isDesktopSearchSuggest() {
      return desktopSuggestMq.matches;
    }

    function syncSearchPlaceholder() {
      if (!(searchInput instanceof HTMLInputElement)) return;
      const desktop = searchInput.dataset.placeholderDesktop || searchInput.placeholder;
      const mobile = searchInput.dataset.placeholderMobile || desktop;
      searchInput.placeholder = desktopSuggestMq.matches ? desktop : mobile;
    }

    syncSearchPlaceholder();

    /** @type {number} */
    let vehicleSuggestTimer = 0;
    /** @type {number} */
    let suggestRequest = 0;
    /** @type {AbortController | null} */
    let suggestAbort = null;
    /** @type {Map<string, { collections: unknown[]; queries: unknown[]; products: unknown[] }>} */
    const suggestCache = new Map();
    const SUGGEST_CACHE_MAX = 16;
    /** @type {{ payload: Record<string, unknown>; query: string } | null} */
    let lastSuggestRender = null;

    /** @type {(() => void) | null} */
    let releaseSearchTrap = null;
    /** @type {HTMLElement | null} */
    let searchPreviousFocus = null;

    /** @returns {string[]} */
    function readRecentSearches() {
      try {
        const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
        if (!raw) return [];
        const stored = JSON.parse(raw);
        return Array.isArray(stored)
          ? stored.filter((item) => typeof item === 'string' && item.trim())
          : [];
      } catch {
        return [];
      }
    }

    /** @param {string} term */
    function saveRecentSearch(term) {
      const trimmed = term.trim();
      if (!trimmed) return;

      const lower = trimmed.toLowerCase();
      const next = [
        trimmed,
        ...readRecentSearches().filter((item) => item.toLowerCase() !== lower),
      ].slice(0, RECENT_SEARCHES_MAX);

      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
      } catch {
        /* storage may be unavailable */
      }
    }

    /** @param {string} term */
    function goToSearch(term) {
      const trimmed = term.trim();
      if (!trimmed) return;
      saveRecentSearch(trimmed);
      window.location.assign(searchResultsUrl(trimmed));
    }

    function renderRecentSearches() {
      if (!(recentList instanceof HTMLElement)) return;

      const items = readRecentSearches();
      recentList.replaceChildren();

      items.forEach((term) => {
        const li = document.createElement('li');
        li.className = 'site-header__search-recent-item';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'site-header__search-recent-btn';
        button.textContent = term;
        button.addEventListener('click', () => {
          goToSearch(term);
        });

        li.append(button);
        recentList.append(li);
      });
    }

    function readGarageVehicle() {
      return window.MAPFitment?.readSelectedVehicle?.() || null;
    }

    /** @param {string} keyword */
    function withVehicleQuery(keyword) {
      const vehicle = readGarageVehicle();
      if (!vehicle?.make) return keyword;
      return [vehicle.make, vehicle.model, vehicle.year, keyword].filter(Boolean).join(' ');
    }

    /** @param {string} keyword */
    function searchResultsUrl(keyword) {
      return `${searchUrl}?type=product&q=${encodeURIComponent(withVehicleQuery(keyword))}`;
    }

    /** Vehicle path-filter suffix (`+make_X+model_Y…`) when a garage vehicle is set. */
    function vehiclePathSuffix() {
      const vehicle = readGarageVehicle();
      if (!vehicle?.make) return '';
      const shopUrl = window.MAPVehicleData?.vehicleShopUrl?.(vehicle);
      if (typeof shopUrl === 'string') {
        const marker = shopUrl.indexOf('/+make_');
        if (marker !== -1) return shopUrl.slice(marker + 1);
      }
      const segments = [];
      if (vehicle.make) segments.push(`+make_${encodeURIComponent(String(vehicle.make).trim())}`);
      if (vehicle.model) segments.push(`+model_${encodeURIComponent(String(vehicle.model).trim())}`);
      if (vehicle.year) segments.push(`+year_${encodeURIComponent(String(vehicle.year).trim())}`);
      if (vehicle.series) segments.push(`+series_${encodeURIComponent(String(vehicle.series).trim())}`);
      return segments.join('');
    }

    /** Collection suggest link — keep the handle, drop Shopify tracking, apply vehicle path. */
    function collectionSuggestUrl(url) {
      try {
        const parsed = new URL(url, window.location.origin);
        let path = parsed.pathname.replace(/\/$/, '');
        const suffix = vehiclePathSuffix();
        if (suffix && !path.includes('+make_')) path = `${path}/${suffix}`;
        return path;
      } catch {
        return url;
      }
    }

    /** @param {Record<string, unknown>} product */
    function productTags(product) {
      const raw = product?.tags;
      if (Array.isArray(raw)) return raw;
      if (typeof raw === 'string') return raw.split(',').map((tag) => tag.trim());
      return [];
    }

    /** @param {Record<string, unknown>} product */
    function productFitsSelectedVehicle(product) {
      const vehicle = readGarageVehicle();
      const fitmentApi = window.MAPFitment;
      if (!vehicle?.make || !fitmentApi) return true;
      return fitmentApi.matchProductFitment(vehicle, { tags: productTags(product) }) === true;
    }

    /** @param {unknown} url */
    function productHandleFromUrl(url) {
      if (typeof url !== 'string' || !url) return '';
      try {
        const path = new URL(url, window.location.origin).pathname;
        const match = path.match(/\/products\/([^/]+)/);
        return match ? decodeURIComponent(match[1]) : '';
      } catch {
        return '';
      }
    }

    /** @param {Record<string, unknown>} product */
    function suggestImageUrl(product) {
      const featured = product?.featured_image;
      const raw =
        (typeof featured === 'string' && featured) ||
        (featured && typeof featured === 'object' && typeof featured.url === 'string' && featured.url) ||
        (typeof product?.image === 'string' && product.image) ||
        '';
      if (!raw.trim()) return '';
      try {
        const sized = new URL(raw, window.location.origin);
        sized.searchParams.set('width', '256');
        return sized.href;
      } catch {
        return raw;
      }
    }

    /** @param {unknown[]} lists */
    function mergeSuggestProducts(lists) {
      const seen = new Set();
      const merged = [];
      lists.forEach((list) => {
        if (!Array.isArray(list)) return;
        list.forEach((product) => {
          const handle = productHandleFromUrl(product?.url) || String(product?.id || '');
          if (!handle || seen.has(handle)) return;
          seen.add(handle);
          merged.push(product);
        });
      });
      return merged;
    }

    /** Fill empty suggest images after first paint (suggest.json often omits them). */
    function hydrateSuggestImages(products, signal) {
      const missing = products.filter((product) => !suggestImageUrl(product));
      return Promise.all(
        missing.slice(0, 3).map((product) => {
          const handle = productHandleFromUrl(product.url);
          if (!handle) return Promise.resolve();
          return fetch(`/products/${encodeURIComponent(handle)}.js`, signal ? { signal } : undefined)
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
              const src =
                (typeof data?.featured_image === 'string' && data.featured_image) ||
                (Array.isArray(data?.images) && typeof data.images[0] === 'string' && data.images[0]) ||
                '';
              if (src) product.image = src;
            })
            .catch(() => {});
        })
      );
    }

    /** @param {string} imageUrl */
    function createSuggestMedia(imageUrl) {
      const media = document.createElement('span');
      media.className = 'site-header__search-product-media';
      const img = document.createElement('img');
      img.className = 'site-header__search-product-image';
      img.src = imageUrl;
      img.alt = '';
      img.width = 128;
      img.height = 96;
      img.decoding = 'async';
      media.append(img);
      return media;
    }

    /** @param {Record<string, unknown>[]} products */
    function paintSuggestImages(products) {
      if (!(searchProducts instanceof HTMLElement)) return;
      products.forEach((product) => {
        const handle = productHandleFromUrl(product.url);
        const imageUrl = suggestImageUrl(product);
        if (!handle || !imageUrl) return;
        const card = searchProducts.querySelector(`[data-suggest-handle="${CSS.escape(handle)}"]`);
        if (!(card instanceof HTMLElement) || card.querySelector('.site-header__search-product-image')) return;
        card.prepend(createSuggestMedia(imageUrl));
      });
    }

    /** @param {Record<string, unknown>} product */
    function suggestSuitsLine(product) {
      const tags = productTags(product);
      let make = '';
      let model = '';
      let year = '';
      tags.forEach((tag) => {
        if (tag.startsWith('make_')) make = tag.slice(5).replace(/-/g, ' ');
        else if (tag.startsWith('model_')) model = tag.slice(6).replace(/-/g, ' ');
        else if (tag.startsWith('year_')) year = tag.slice(5);
      });
      if (!make && !model) return '';
      return [suitsPrefix, make, model, year].filter(Boolean).join(' ');
    }

    /**
     * Predictive search JSON prices are shop-currency dollars ("104.92"), not
     * cents. Treating them as cents produced ~$1.05 instead of the PDP price.
     * @param {unknown} value
     */
    function suggestPriceToCents(value) {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return NaN;
      return Math.round(amount * 100);
    }

    /** GST-inclusive money — same path as gst-money.liquid / MapUtils. */
    function formatSuggestMoney(value) {
      const cents = suggestPriceToCents(value);
      if (!Number.isFinite(cents)) return '';
      if (window.MapUtils && typeof window.MapUtils.formatMoney === 'function') {
        return window.MapUtils.formatMoney(cents, gstRate, moneyFormat);
      }
      return '';
    }

    function hideSuggestions() {
      if (searchSuggest instanceof HTMLElement) searchSuggest.hidden = true;
    }

    function cancelSuggest() {
      suggestAbort?.abort();
      window.clearTimeout(vehicleSuggestTimer);
    }

    /** @param {string} query */
    function showCachedSuggestionsOrHide(query) {
      if (!paintSuggestCache(query)) hideSuggestions();
    }

    /** @param {string} query */
    function showRecentOrEmpty(query) {
      const recentSection = recentList?.closest('.site-header__search-recent');
      const hasQuery = Boolean(query);
      if (searchField instanceof HTMLElement) searchField.classList.toggle('is-typed', hasQuery);
      if (searchClear instanceof HTMLElement) searchClear.hidden = !hasQuery;
      if (searchSubmit instanceof HTMLElement) {
        const vehicleSet =
          (searchVehicleBtn instanceof HTMLElement && searchVehicleBtn.classList.contains('is-vehicle-set')) ||
          Boolean(root.querySelector('.site-header__search-wrap.is-vehicle-set'));
        searchSubmit.hidden = !hasQuery || vehicleSet;
      }
      if (searchVehicleBtn instanceof HTMLElement) {
        const vehicleSet = searchVehicleBtn.classList.contains('is-vehicle-set');
        const hideCar = hasQuery && !vehicleSet;
        searchVehicleBtn.hidden = hideCar;
        searchVehicleBtn.setAttribute('aria-hidden', hideCar ? 'true' : 'false');
      }
      if (recentSection instanceof HTMLElement) {
        const empty = !(recentList instanceof HTMLElement) || recentList.childElementCount === 0;
        recentSection.hidden = hasQuery || empty;
      }
      if (!hasQuery) hideSuggestions();
    }

    function renderSuggestions(payload, query) {
      if (!(searchQueries instanceof HTMLElement) || !(searchProducts instanceof HTMLElement)) return;
      lastSuggestRender = { payload, query };

      const results = payload?.resources?.results || {};
      const queries = Array.isArray(results.queries) ? results.queries : [];
      const products = Array.isArray(results.products) ? results.products : [];

      searchQueries.replaceChildren();

      const collections = Array.isArray(results.collections) ? results.collections : [];
      collections.slice(0, 4).forEach((item) => {
        const title = typeof item?.title === 'string' ? item.title : '';
        const url = typeof item?.url === 'string' ? item.url : '';
        if (!title || !url) return;
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'site-header__search-suggest-query';
        link.href = collectionSuggestUrl(url);
        link.textContent = title;
        li.append(link);
        searchQueries.append(li);
      });

      if (searchQueries.childElementCount < 4) {
        queries.slice(0, 4 - searchQueries.childElementCount).forEach((item) => {
          const text = typeof item?.text === 'string' ? item.text : '';
          if (!text) return;
          const li = document.createElement('li');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'site-header__search-suggest-query';
          button.textContent = text;
          button.addEventListener('click', () => {
            goToSearch(text);
          });
          li.append(button);
          searchQueries.append(li);
        });
      }

      searchProducts.replaceChildren();
      const showProductCards = isDesktopSearchSuggest();
      const matched = showProductCards
        ? readGarageVehicle()?.make
          ? products.filter(productFitsSelectedVehicle)
          : products
        : [];
      const withImage = [];
      const withoutImage = [];
      matched.forEach((product) => {
        if (suggestImageUrl(product)) withImage.push(product);
        else withoutImage.push(product);
      });
      const visibleProducts = withImage.concat(withoutImage).slice(0, 3);
      visibleProducts.forEach((product) => {
        const url = typeof product?.url === 'string' ? product.url : '';
        const title = typeof product?.title === 'string' ? product.title : '';
        if (!url || !title) return;

        const card = document.createElement('a');
        card.className = 'site-header__search-product';
        card.href = url;
        const handle = productHandleFromUrl(url);
        if (handle) card.dataset.suggestHandle = handle;

        const imageUrl = suggestImageUrl(product);
        if (imageUrl) card.append(createSuggestMedia(imageUrl));

        const info = document.createElement('span');
        info.className = 'site-header__search-product-info';

        const names = document.createElement('span');
        names.className = 'site-header__search-product-copy';
        const name = document.createElement('span');
        name.className = 'site-header__search-product-title';
        name.textContent = title;
        names.append(name);
        const suits = suggestSuitsLine(product);
        if (suits) {
          const suitsEl = document.createElement('span');
          suitsEl.className = 'site-header__search-product-suits';
          suitsEl.textContent = suits;
          names.append(suitsEl);
        }
        info.append(names);

        const pricing = document.createElement('span');
        pricing.className = 'site-header__search-product-pricing';
        const price = document.createElement('span');
        price.className = 'site-header__search-product-price';
        price.textContent = formatSuggestMoney(product.price);
        pricing.append(price);
        info.append(pricing);
        card.append(info);
        searchProducts.append(card);
      });

      if (searchViewAll instanceof HTMLAnchorElement) {
        searchViewAll.href = searchResultsUrl(query);
        searchViewAll.hidden = !showProductCards;
      }

      const queriesWrap = searchQueries.closest('.site-header__search-suggest-queries');
      if (queriesWrap instanceof HTMLElement) queriesWrap.hidden = searchQueries.childElementCount === 0;

      const hasAnything = searchQueries.childElementCount > 0 || searchProducts.childElementCount > 0;
      if (searchSuggest instanceof HTMLElement) searchSuggest.hidden = !hasAnything;
    }

    /**
     * @param {string} q
     * @param {AbortSignal} signal
     */
    function fetchSuggestJson(q, signal) {
      const params = new URLSearchParams();
      params.set('q', q);
      params.set('resources[type]', 'product,collection,query');
      params.set('resources[limit]', '6');
      params.set('resources[limit_scope]', 'each');
      params.set('resources[options][unavailable_products]', 'last');
      return fetch(`/search/suggest.json?${params}`, { headers: { Accept: 'application/json' }, signal }).then((r) =>
        r.ok ? r.json() : null
      );
    }

    /** @param {string} query */
    function suggestCacheKey(query) {
      return query.trim().toLowerCase();
    }

    /**
     * @param {string} query
     * @param {{ collections: unknown[]; queries: unknown[]; products: unknown[] }} entry
     */
    function writeSuggestCache(query, entry) {
      const key = suggestCacheKey(query);
      suggestCache.delete(key);
      suggestCache.set(key, entry);
      while (suggestCache.size > SUGGEST_CACHE_MAX) {
        const oldest = suggestCache.keys().next().value;
        if (oldest === undefined) break;
        suggestCache.delete(oldest);
      }
    }

    /** @param {unknown} value */
    function suggestText(value) {
      if (typeof value === 'string') return value;
      if (value && typeof value === 'object') {
        const record = /** @type {Record<string, unknown>} */ (value);
        if (typeof record.title === 'string') return record.title;
        if (typeof record.text === 'string') return record.text;
      }
      return '';
    }

    /**
     * @param {{ collections: unknown[]; queries: unknown[]; products: unknown[] }} entry
     * @param {string} query
     */
    function filterSuggestCache(entry, query) {
      const needle = query.trim().toLowerCase();
      const matches = (item) => suggestText(item).toLowerCase().includes(needle);
      return {
        collections: entry.collections.filter(matches),
        queries: entry.queries.filter(matches),
        products: entry.products.filter(matches),
      };
    }

    /** Instant paint from a prior response so typing does not wait on Shopify. */
    function paintSuggestCache(query) {
      const key = suggestCacheKey(query);
      let entry = suggestCache.get(key) || null;
      if (!entry) {
        let bestKey = '';
        suggestCache.forEach((_value, cached) => {
          if (key.startsWith(cached) && cached.length > bestKey.length) bestKey = cached;
        });
        if (bestKey) entry = suggestCache.get(bestKey) || null;
      }
      if (!entry) return false;
      const filtered = suggestCache.has(key) ? entry : filterSuggestCache(entry, query);
      if (!filtered.collections.length && !filtered.queries.length && !filtered.products.length) {
        return false;
      }
      renderSuggestions({ resources: { results: filtered } }, query);
      return true;
    }

    /**
     * @param {string} query
     * @param {unknown[]} collections
     * @param {unknown[]} queries
     * @param {unknown[]} products
     * @param {AbortSignal} signal
     */
    function paintSuggestNetwork(query, collections, queries, products, signal) {
      writeSuggestCache(query, { collections, queries, products });
      renderSuggestions(
        {
          resources: {
            results: { collections, queries, products },
          },
        },
        query
      );
      if (!isDesktopSearchSuggest()) return;
      hydrateSuggestImages(products, signal).then(() => {
        if (signal.aborted) return;
        paintSuggestImages(products);
      });
    }

    /** @param {string} query */
    function fetchSuggestions(query) {
      const requestId = ++suggestRequest;
      cancelSuggest();
      const controller = new AbortController();
      suggestAbort = controller;
      const { signal } = controller;
      const vehiclePrefixed = withVehicleQuery(query);
      const partTag =
        window.MapUtils && typeof window.MapUtils.partNumberTagQuery === 'function'
          ? window.MapUtils.partNumberTagQuery(query)
          : '';
      /** @type {unknown[]} */
      let keywordProducts = [];
      /** @type {unknown[]} */
      let partProducts = [];
      /** @type {unknown[]} */
      let keywordCollections = [];
      /** @type {unknown[]} */
      let keywordQueries = [];

      function paintMerged(extraProducts) {
        const products = mergeSuggestProducts([keywordProducts, partProducts, extraProducts]);
        paintSuggestNetwork(query, keywordCollections, keywordQueries, products, signal);
      }

      fetchSuggestJson(query, signal)
        .then((payload) => {
          if (requestId !== suggestRequest) return;
          if (!payload) {
            showCachedSuggestionsOrHide(query);
            return;
          }
          keywordCollections = payload?.resources?.results?.collections || [];
          keywordQueries = payload?.resources?.results?.queries || [];
          keywordProducts = mergeSuggestProducts([payload?.resources?.results?.products]);
          paintMerged();
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return;
          if (requestId !== suggestRequest) return;
          showCachedSuggestionsOrHide(query);
        });

      if (partTag) {
        fetchSuggestJson(partTag, signal)
          .then((payload) => {
            if (requestId !== suggestRequest || !payload) return;
            partProducts = mergeSuggestProducts([payload?.resources?.results?.products]);
            paintMerged();
          })
          .catch((error) => {
            if (error?.name === 'AbortError') return;
          });
      }

      if (vehiclePrefixed === query) return;

      vehicleSuggestTimer = window.setTimeout(() => {
        if (requestId !== suggestRequest) return;
        fetchSuggestJson(vehiclePrefixed, signal)
          .then((vehiclePayload) => {
            if (requestId !== suggestRequest || !vehiclePayload) return;
            paintMerged(vehiclePayload?.resources?.results?.products);
          })
          .catch((error) => {
            if (error?.name === 'AbortError') return;
          });
      }, 280);
    }

    function onSearchInput() {
      if (!(searchInput instanceof HTMLInputElement)) return;
      const query = searchInput.value.trim();
      showRecentOrEmpty(query);
      if (!query) {
        cancelSuggest();
        return;
      }
      paintSuggestCache(query);
      fetchSuggestions(query);
    }

    function finishSearchClose() {
      searchHideTimer = null;
      if (!searchPanel || isSearchOpen()) return;

      searchPanel.hidden = true;
      hideSuggestions();
      if (searchBackdrop) searchBackdrop.hidden = true;
    }

    function closeSearchPanel() {
      if (!searchPanel || !isSearchOpen()) return;

      if (searchHideTimer) {
        window.clearTimeout(searchHideTimer);
        searchHideTimer = null;
      }

      cancelSuggest();
      root.classList.remove('is-search-open');
      searchTrigger?.setAttribute('aria-expanded', 'false');
      unlockScroll();
      releaseSearchTrap?.();
      releaseSearchTrap = null;

      if (searchPreviousFocus instanceof HTMLElement) {
        searchPreviousFocus.focus({ preventScroll: true });
      }
      searchPreviousFocus = null;

      // Out of the tab order for the whole fade, not just once hidden lands.
      searchPanel.inert = true;

      const delay = prefersReducedMotion() ? 0 : SEARCH_MS;
      searchHideTimer = window.setTimeout(finishSearchClose, delay);
    }

    function openSearchPanel() {
      if (!searchPanel || !searchTrigger || isSearchOpen()) return;

      if (searchHideTimer) {
        window.clearTimeout(searchHideTimer);
        searchHideTimer = null;
      }

      closeDrawer();
      closeAllMegaGroups();

      searchPreviousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      renderRecentSearches();
      if (searchInput instanceof HTMLInputElement) showRecentOrEmpty(searchInput.value.trim());
      searchPanel.hidden = false;
      searchPanel.inert = false;
      if (searchBackdrop) searchBackdrop.hidden = false;
      searchTrigger.setAttribute('aria-expanded', 'true');
      lockScroll();

      if (searchPanel instanceof HTMLElement) {
        void searchPanel.offsetWidth;
        releaseSearchTrap = createFocusTrap(searchPanel, closeSearchPanel);
      }

      root.classList.add('is-search-open');

      if (searchInput instanceof HTMLElement) {
        searchInput.focus({ preventScroll: true });
      }
    }

    searchTrigger?.addEventListener('click', (event) => {
      event.preventDefault();
      if (isSearchOpen()) closeSearchPanel();
      else openSearchPanel();
    });

    searchBackdrop?.addEventListener('click', closeSearchPanel);

    desktopSuggestMq.addEventListener('change', () => {
      syncSearchPlaceholder();
      if (!lastSuggestRender || searchSuggest?.hidden) return;
      renderSuggestions(lastSuggestRender.payload, lastSuggestRender.query);
      if (!isDesktopSearchSuggest()) return;
      const products = lastSuggestRender.payload?.resources?.results?.products;
      if (!Array.isArray(products) || !products.length) return;
      hydrateSuggestImages(products).then(() => paintSuggestImages(products));
    });

    searchInput?.addEventListener('input', onSearchInput);

    window.addEventListener('map:vehicle-selected', () => {
      if (searchInput instanceof HTMLInputElement) showRecentOrEmpty(searchInput.value.trim());
    });

    searchClear?.addEventListener('click', () => {
      if (!(searchInput instanceof HTMLInputElement)) return;
      searchInput.value = '';
      searchInput.focus({ preventScroll: true });
      onSearchInput();
    });

    searchForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!(searchInput instanceof HTMLInputElement)) return;
      goToSearch(searchInput.value);
    });

    searchViewAll?.addEventListener('click', (event) => {
      event.preventDefault();
      if (!(searchInput instanceof HTMLInputElement)) return;
      goToSearch(searchInput.value);
    });

    searchProducts?.addEventListener('click', (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('a')) return;
      if (searchInput instanceof HTMLInputElement) saveRecentSearch(searchInput.value);
    });

    document.addEventListener('click', (event) => {
      if (!isSearchOpen() || !(event.target instanceof Node)) return;
      if (searchPanel.contains(event.target)) return;
      if (searchTrigger?.contains(event.target)) return;

      closeSearchPanel();
    });

    searchPanel?.querySelectorAll('[data-map-drawer-open="HeaderVehicleDrawer"]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        closeSearchPanel();
      });
    });

    const vehicleDrawer = root.querySelector('#HeaderVehicleDrawer');
    vehicleDrawer?.querySelectorAll('[data-vehicle-find-panel]').forEach((panel) => {
      if (panel instanceof HTMLElement) MapUI?.initVehicleFindPanel?.(panel);
    });

    // Vehicle pills report the drawer state (WIL-538 a11y).
    const syncVehiclePillExpanded = (expanded) => {
      document.querySelectorAll('[data-header-vehicle-pill]').forEach((pill) => {
        pill.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    };
    vehicleDrawer?.addEventListener('map:drawer-open', () => syncVehiclePillExpanded(true));
    vehicleDrawer?.addEventListener('map:drawer-close', () => syncVehiclePillExpanded(false));

    root.querySelector('#HeaderLocationDrawer')?.querySelectorAll('[data-location-find-panel]').forEach((panel) => {
      if (panel instanceof HTMLElement) MapUI?.initLocationFindPanel?.(panel);
    });

    MapUI?.initModals?.();

    const headerBar = root.querySelector('.site-header__bar');
    const header = root.querySelector('.site-header__sticky');

    function syncHeaderOffset() {
      const measure =
        header instanceof HTMLElement
          ? header
          : headerBar instanceof HTMLElement
            ? headerBar
            : null;
      if (measure) {
        const rect = measure.getBoundingClientRect();
        document.documentElement.style.setProperty(
          '--site-header-bar-offset',
          `${Math.max(0, Math.ceil(rect.bottom))}px`
        );
      }

      const search = root.querySelector('.site-header__search-wrap');
      const menuTop =
        search instanceof HTMLElement && search.getBoundingClientRect().height > 0
          ? search.getBoundingClientRect().top
          : root.querySelector('.site-header__primary')?.getBoundingClientRect().bottom ?? 0;
      document.documentElement.style.setProperty(
        '--site-header-menu-offset',
        `${Math.max(0, Math.floor(menuTop))}px`
      );
    }

    const sentinel = root.querySelector('.site-header__sticky-sentinel');
    if (sentinel instanceof HTMLElement) {
      let lastStuck = null;
      let stuckRaf = 0;

      function applyStuckState() {
        const rect = sentinel.getBoundingClientRect();
        const stuck = !(rect.top >= 0 && rect.bottom > 0) && window.scrollY > 0;
        if (stuck === lastStuck) return;
        lastStuck = stuck;
        root.classList.toggle('is-stuck', stuck);
        syncHeaderOffset();
        root.dispatchEvent(
          new CustomEvent('map:header-stuck', { bubbles: true, detail: { stuck } })
        );
      }

      function syncStuckState() {
        if (stuckRaf) return;
        stuckRaf = window.requestAnimationFrame(() => {
          stuckRaf = 0;
          applyStuckState();
        });
      }

      applyStuckState();
      window.addEventListener('scroll', syncStuckState, { passive: true });
      window.addEventListener('resize', syncStuckState);
    }
  }

  function boot() {
    document.querySelectorAll('[data-site-header]').forEach((root) => {
      if (root instanceof HTMLElement && root.dataset.headerInit !== 'true') {
        root.dataset.headerInit = 'true';
        initHeader(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme-editor section reloads replace the header DOM — re-init the new
  // element (dataset-guarded, so an untouched header is unaffected).
  document.addEventListener('shopify:section:load', boot);
})();
